import { Link, useRoute } from "wouter";
import {
  CalendarDays,
  ExternalLink,
  MapPin,
  Trophy,
  Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useOrgPublic } from "@/lib/queries";

// Public front door for an org. No auth required — used as a share landing
// from social, business cards, league flyers. Renders org branding, next
// few events, top of the leaderboard, and recent finished games.
export default function OrgPublic() {
  const [, params] = useRoute("/:orgSlug");
  const slug = params?.orgSlug ?? null;
  const { data, isLoading, error } = useOrgPublic(slug);

  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center p-8">
        <div className="text-center space-y-3 max-w-md">
          <h1 className="text-2xl font-display uppercase tracking-tight">
            Not found
          </h1>
          <p className="text-sm text-muted-foreground">
            No Cornholio org at <code className="bg-muted px-1 py-0.5 rounded">/{slug}</code>.
          </p>
          <Link
            href="/"
            className="text-primary hover:underline text-sm inline-flex items-center gap-1"
          >
            cornholio.app <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    );
  }

  const { org, upcomingEvents, leaderboard, recentGames } = data;
  const tagline = org.brandConfig?.tagline;
  const primary = org.brandConfig?.primaryColor;

  return (
    <div className="min-h-dvh bg-background">
      <header
        className="border-b border-border"
        style={primary ? { borderTopColor: primary, borderTopWidth: 4 } : undefined}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-2">
          <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">
            Cornholio · /{org.slug}
          </p>
          <h1 className="text-3xl sm:text-5xl font-display uppercase tracking-tight">
            {org.name}
          </h1>
          {tagline && (
            <p className="text-muted-foreground text-sm sm:text-base">
              {tagline}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        <section className="space-y-3">
          <h2 className="text-xs font-display uppercase tracking-widest text-muted-foreground inline-flex items-center gap-2">
            <CalendarDays size={12} /> Upcoming events
          </h2>
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No events scheduled yet — check back soon.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-card overflow-hidden">
              {upcomingEvents.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center gap-3 p-3 text-sm"
                >
                  <div className="shrink-0 w-12 text-center">
                    <DateChip date={e.startDate} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
                      <span className="inline-flex items-center gap-1">
                        <Users size={11} /> {e.format}
                      </span>
                      {e.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {e.location}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h2 className="text-xs font-display uppercase tracking-widest text-muted-foreground inline-flex items-center gap-2">
              <Trophy size={12} /> Top players
            </h2>
            {leaderboard.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No completed games yet.
              </p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <ol className="divide-y divide-border">
                    {leaderboard.map((row) => (
                      <li
                        key={row.playerId}
                        className="flex items-center gap-3 p-3 text-sm"
                      >
                        <span className="w-6 text-center font-display tabular-nums text-muted-foreground">
                          {row.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{row.name}</div>
                          <div className="text-xs text-muted-foreground">
                            @{row.slug}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-display tabular-nums">
                            {Math.round(row.winPct * 100)}%
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {row.wins}–{row.losses}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="text-xs font-display uppercase tracking-widest text-muted-foreground">
              Recent results
            </h2>
            {recentGames.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing logged yet.
              </p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <ul className="divide-y divide-border">
                    {recentGames.map((g) => (
                      <li key={g.id}>
                        <Link
                          href={`/g/${g.shareCode}`}
                          className="flex items-center gap-3 p-3 text-sm hover:bg-muted/50"
                        >
                          <div className="flex-1 min-w-0">
                            <div
                              className={
                                g.winningTeam === 1
                                  ? "font-semibold truncate"
                                  : "text-muted-foreground truncate"
                              }
                            >
                              {g.team1Players.map((p) => p.name).join(" & ") || "—"}
                            </div>
                            <div
                              className={
                                g.winningTeam === 2
                                  ? "font-semibold truncate"
                                  : "text-muted-foreground truncate"
                              }
                            >
                              {g.team2Players.map((p) => p.name).join(" & ") || "—"}
                            </div>
                          </div>
                          <div className="shrink-0 grid grid-rows-2 text-right font-display tabular-nums leading-tight px-2 py-1 rounded border border-border bg-background min-w-[3rem]">
                            <span
                              className={
                                g.winningTeam === 1
                                  ? "text-primary"
                                  : "text-muted-foreground"
                              }
                            >
                              {g.team1Score}
                            </span>
                            <span
                              className={
                                g.winningTeam === 2
                                  ? "text-primary"
                                  : "text-muted-foreground"
                              }
                            >
                              {g.team2Score}
                            </span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        <footer className="pt-6 border-t border-border text-xs text-muted-foreground flex items-center justify-between gap-2 flex-wrap">
          <Link href="/" className="hover:text-foreground">
            Powered by cornholio.app
          </Link>
          <span>{org.timezone}</span>
        </footer>
      </main>
    </div>
  );
}

function DateChip({ date }: { date: string }) {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return <span className="text-xs">{date}</span>;
  const dt = new Date(y, m - 1, d);
  const month = dt.toLocaleDateString(undefined, { month: "short" });
  return (
    <div className="rounded border border-border bg-background py-1 leading-none">
      <div className="text-[9px] font-display uppercase tracking-widest text-muted-foreground">
        {month}
      </div>
      <div className="text-lg font-display tabular-nums">{d}</div>
    </div>
  );
}

